
import ServiceTemplate from '../../ServiceTemplate';
import SEOHead from '../../../../components/SEOHead';

export default function CommercialElectricalInstallationsPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://electriciansolutionsfresno.com';

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: 'Commercial Electrical Installations',
    provider: {
      '@type': 'ElectricalContractor',
      name: 'Fresno Electrical Services',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Fresno',
        addressRegion: 'CA',
        addressCountry: 'US',
      },
    },
    areaServed: {
      '@type': 'City',
      name: 'Fresno',
      '@id': 'https://en.wikipedia.org/wiki/Fresno,_California',
    },
    description:
      'Professional commercial electrical installation services in Fresno CA for new construction, tenant improvements, and business electrical systems',
    url: `${siteUrl}/services/commercial-specialist/commercial-electrical-installations`,
  };

  const features = [
    {
      icon: 'ri-building-line',
      title: 'New Construction Electrical',
      description: 'Complete electrical installations for new commercial buildings in Fresno',
    },
    {
      icon: 'ri-tools-line',
      title: 'Tenant Improvement Electrical',
      description: 'Electrical fit-outs and modifications for commercial tenant spaces',
    },
    {
      icon: 'ri-flashlight-line',
      title: 'Power Distribution Systems',
      description: 'Main service panels, sub-panels, and distribution equipment installation',
    },
    {
      icon: 'ri-lightbulb-line',
      title: 'Commercial Lighting Systems',
      description: 'Energy-efficient LED lighting design and installation for businesses',
    },
    {
      icon: 'ri-plug-line',
      title: 'Outlet & Circuit Installation',
      description: 'Dedicated circuits for equipment, computers, and business operations',
    },
    {
      icon: 'ri-server-line',
      title: 'Data & Communication Wiring',
      description: 'Electrical infrastructure for IT systems and communication networks',
    },
  ];

  return (
    <>
      <SEOHead
        title="Commercial Electrical Installations Fresno | Business Electrician CA"
        description="Expert commercial electrical installation services in Fresno CA. New construction, tenant improvements, power distribution, and business electrical systems. Licensed C-10 contractors."
        canonical="/services/commercial-specialist/commercial-electrical-installations"
        keywords="commercial electrical installation Fresno, business electrician, tenant improvement electrical, new construction electrical"
        schema={schema}
      />
      <ServiceTemplate
        heroTitle="Commercial Electrical Installations — Fresno, CA"
        heroSubtitle="Professional electrical installation services for Fresno businesses and commercial properties"
        heroImage="https://public.readdy.ai/ai/img_res/bd7961799eff4eacfd78c3f9b75b514d.jpg"
        fresnoImage="https://readdy.ai/api/search-image?query=Commercial%20electrical%20installation%20with%20cable%20trays%20and%20conduit%20systems%20mounted%20on%20industrial%20ceiling%20with%20junction%20boxes%20and%20organized%20wiring%20infrastructure%20in%20warehouse%20setting&width=700&height=600&seq=comm-install-fresno-001&orientation=portrait"
        fresnoContent="Serving Fresno's commercial construction and business community with expert electrical installation services. From downtown office buildings to retail spaces in the Tower District and industrial facilities near Highway 99, we provide complete electrical installations for new construction and tenant improvements. Our licensed C-10 electricians deliver code-compliant work that meets California Title 24 requirements and Fresno building codes, ensuring your business has safe, reliable electrical infrastructure from day one."
        features={features}
        faqs={[
          {
            question: 'What commercial electrical installation services do you provide in Fresno?',
            answer:
              'We provide comprehensive commercial electrical installation services throughout Fresno CA, including complete electrical systems for new construction, tenant improvement electrical fit-outs, power distribution panel installation, commercial lighting systems, dedicated equipment circuits, three-phase power installation, emergency lighting, exit signs, and electrical infrastructure for IT and communication systems. We serve offices, retail stores, restaurants, warehouses, medical facilities, and industrial buildings across Fresno and Clovis. All installations are performed by licensed C-10 electricians and meet California Title 24 energy codes and NEC standards.',
          },
          {
            question: 'How long does a commercial electrical installation take in Fresno?',
            answer:
              'Commercial electrical installation timelines in Fresno vary based on project scope and building size. Small tenant improvement projects typically take 1-3 weeks. Medium-sized office or retail fit-outs average 3-6 weeks. Large commercial buildings or industrial facilities may require 2-6 months for complete electrical installation. We provide detailed project schedules during the planning phase and coordinate closely with general contractors, property managers, and other trades to keep your project on schedule. Rush services are available for time-sensitive business openings.',
          },
          {
            question: 'Do you handle permits and inspections for commercial electrical work in Fresno?',
            answer:
              'Yes, we handle all permitting and inspection coordination for commercial electrical installations in Fresno CA. We pull permits through the City of Fresno Building Department, ensure all work meets California electrical codes and Title 24 energy requirements, coordinate inspections with city inspectors, and provide all required documentation for final approval. Our licensed C-10 contractors are familiar with Fresno building department requirements and maintain excellent relationships with local inspectors to ensure smooth project approval.',
          },
          {
            question: 'Can you work around our business hours in Fresno?',
            answer:
              'Absolutely. We offer flexible scheduling to minimize disruption to your Fresno business operations. Many commercial electrical installations are completed during evenings, weekends, or overnight hours when your business is closed. For larger projects, we can work in phases to keep portions of your business operational during construction. We coordinate closely with property managers, business owners, and general contractors to create a work schedule that meets your operational needs and project deadlines.',
          },
          {
            question: 'What is the cost of commercial electrical installation in Fresno?',
            answer:
              'Commercial electrical installation costs in Fresno vary widely based on building size, electrical load requirements, and project complexity. Small tenant improvement electrical work typically ranges from $5,000-$25,000. Medium-sized office or retail electrical installations average $25,000-$75,000. Large commercial buildings or industrial facilities may require $75,000-$500,000+ for complete electrical systems. We provide detailed written estimates after reviewing plans and conducting site surveys. Many Fresno businesses qualify for energy rebates through PG&E when installing energy-efficient lighting and electrical systems.',
          },
          {
            question: 'Are you licensed and insured for commercial electrical work in Fresno?',
            answer:
              "Yes, we hold a California C-10 Electrical Contractor license, which is required for all commercial electrical work in Fresno CA. Our electricians are trained in commercial electrical systems, three-phase power, fire alarm systems, and California Title 24 compliance. We carry full commercial general liability insurance and workers' compensation coverage to protect your property and our team. All work is permitted through the City of Fresno and inspected for code compliance. We provide certificates of insurance upon request for property managers and general contractors.",
          },
        ]}
      />
    </>
  );
}
